import { AfterViewInit, Component, ElementRef, forwardRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ReplaySubject } from 'rxjs';
import tinymce from 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/plugins/paste';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/table';
import 'tinymce/plugins/code';

@Component({
  selector: 'app-markdown',
  templateUrl: './markdown.component.html',
  styleUrls: ['./markdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MarkdownComponent),
      multi: true,
    },
  ],
})
export class MarkdownComponent implements AfterViewInit, ControlValueAccessor, OnDestroy {
  @ViewChild('editor', { static: true }) div: ElementRef;

  @Input() options: any;
  @Input() id: string;
  @Input() toolbar = ['bold italic | bullist numlist outdent indent', 'link | code'];

  public editor: any;
  disabled = new ReplaySubject<boolean>(1);
  content;
  _change;

  writeValue(obj: any, tries?: number): void {
    if (this.editor?.initialized) {
      const container = this.editor.getContainer();
      if (container) {
        container.style.height = '165px';
      }
      this.editor.setContent(obj || '');
      return;
    }

    if (tries == 20) {
      console.error('Could not set markdown content!', obj);
    }

    setTimeout(() => {
      this.writeValue(obj, tries ? tries + 1 : 1);
    }, 100);
  }

  registerOnChange(fn: any): void {
    this._change = fn;
  }

  registerOnTouched(fn: any): void {}
  setDisabledState(isDisabled: boolean): void {
    console.log('setting disabled editor', isDisabled);
    this.disabled.next(isDisabled);
  }

  ngAfterViewInit() {
    tinymce.init({
      target: this.div.nativeElement,

      branding: false, // Disable the "Powered by Tiny" branding
      statusbar: true,
      base_url: '/assets/tinymce', // This should be the base URL where TinyMCE is located
      code_dialog_height: 400,
      code_dialog_width: 600,
      height: 165,

      menubar: false,
      forced_root_block: 'div',
      plugins: ['lists', 'paste'],
      toolbar: 'undo redo | bold italic underline | numlist bullist | clearContent',
      paste_word_valid_elements: 'div[style],strong/b,em,span[style],a[href],ul,ol,li,h1,h2,h3,h4,h5,h6,p',
      valid_elements: 'div[style],strong/b,em,span[style],a[href],ul,ol,li,p',
      fixed_toolbar_container: '#toolbar-container',
      content_style:
        "@import url('https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap'); body {margin: 12px 16px 0 16px;} .mce-content-body div { margin-top: 0 !important; font-family: Lato; font-size: 16px; color: #111010; font-weight: 500;}",
      valid_styles: {
        '*': 'font-size,font-family,color,text-decoration,text-align',
      },

      setup: editor => {
        this.editor = editor;
        editor.on('Change KeyUp', () => this.editorChange());
        this.disabled.subscribe(disabled => {
          editor.setMode(disabled ? 'readonly' : 'design');
        });
      },
      ...this.options,
    });
  }

  editorChange() {
    if (this.editor) {
      const content = this.editor.getContent();
      this.changed(content);
    }
  }

  changed(value) {
    if (this._change) {
      this._change(value);
    }
  }
  ngOnDestroy() {
    if (this.editor) {
      tinymce.remove(this.editor);
    }
  }
}
